import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";

import {
  MaterializationType,
  PublishedModelsQuery,
  usePublishedModelsQuery,
  useUpdateModelTableReferencesQuery,
} from "@/apollo/types";

// Context returning the live published models and loading state
type PublishedModelsState = {
  publishedModels: PublishedModelsQuery["publishedModels"];
  loadingModels: boolean;
};

const PublishedModelsContext = createContext<PublishedModelsState | null>(null);

// Context returning a stable getter function for published models
type PublishedModelsGetter = () => PublishedModelsQuery["publishedModels"];

const PublishedModelsGetterContext =
  createContext<PublishedModelsGetter | null>(null);

export const PublishedModelsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { data, loading } = usePublishedModelsQuery();

  const hasPendingUpdates = useMemo(() => {
    return (data?.publishedModels ?? []).some(
      (model) =>
        model.materializationType === MaterializationType.Table &&
        (!model.dwTable || model.dwTable.status === "RUNNING"),
    );
  }, [data]);

  //Poll for updates for table references, when there is a pending update
  //Apollo will automatically put the updated tables in the cache for the list of models.
  useUpdateModelTableReferencesQuery({
    pollInterval: 60_000,
    skipPollAttempt() {
      return document.hidden;
    },
    skip: !hasPendingUpdates,
  });

  const stateValue = useMemo<PublishedModelsState>(() => {
    const publishedModels = data?.publishedModels ?? [];
    return {
      publishedModels,
      loadingModels: loading,
    };
  }, [data?.publishedModels, loading]);

  // Create a ref to hold the current publishedModels value.
  const publishedModelsRef = useRef<PublishedModelsQuery["publishedModels"]>(
    stateValue.publishedModels,
  );

  // Update the ref whenever the publishedModels part of our state changes.
  useEffect(() => {
    publishedModelsRef.current = stateValue.publishedModels;
  }, [stateValue.publishedModels]);

  // Stable getter function that always returns the latest published models.
  const getPublishedModels = useCallback(() => {
    return publishedModelsRef.current;
  }, []);

  return (
    <PublishedModelsContext.Provider value={stateValue}>
      <PublishedModelsGetterContext.Provider value={getPublishedModels}>
        {children}
      </PublishedModelsGetterContext.Provider>
    </PublishedModelsContext.Provider>
  );
};

// Hook for components that want the published models and loading status (will re-render on changes)
export const usePublishedModels = () => {
  const context = useContext(PublishedModelsContext);
  if (!context) {
    throw new Error(
      "usePublishedModels must be used within a PublishedModelsProvider",
    );
  }
  return context;
};

// Hook for components that want a stable getter to get the published models at any time
// without causing a component to re-render when changes occur.
export const usePublishedModelsGetter = () => {
  const getPublishedModels = useContext(PublishedModelsGetterContext);
  if (!getPublishedModels) {
    throw new Error(
      "usePublishedModelsGetter must be used within a PublishedModelsProvider",
    );
  }
  return getPublishedModels;
};
